/** Cota as modalidades reais da Shopify/Frenet sem alterar o carrinho da cliente. */
const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const escapeShippingText = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class ShippingCalculator extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-cep-input]');
    this.button = this.querySelector('[data-cep-button]');
    this.results = this.querySelector('[data-cep-results]');
    this.error = this.querySelector('[data-cep-error]');
    if (!this.input || !this.button || !this.results || !this.error) return;
    this.variantId = Number(this.dataset.variantId);
    this.threshold = Number(this.dataset.freeThreshold) || 0;
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.results.setAttribute('aria-live', 'polite');
    this.error.setAttribute('role', 'alert');
    this.input.addEventListener('input', () => this.maskCep(), options);
    this.button.addEventListener('click', () => this.calculate(), options);
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') { event.preventDefault(); this.calculate(); }
    }, options);
    (this.closest('.shopify-section') || document).addEventListener('variant:update', (event) => {
      const variant = event.detail?.resource;
      if (!variant?.id) return;
      this.variantId = Number(variant.id);
      if (this.results.textContent.trim() || this.request) this.calculate();
    }, options);
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.request?.abort();
  }

  maskCep() {
    const value = this.input.value.replace(/\D/g, '').slice(0, 8);
    this.input.value = value.length > 5 ? `${value.slice(0, 5)}-${value.slice(5)}` : value;
    this.request?.abort();
    this.results.replaceChildren();
    this.hideError();
  }

  async graphql(query, variables, signal) {
    const response = await fetch('/api/2026-10/graphql.json', {
      method: 'POST', signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json, multipart/mixed' },
      body: JSON.stringify({ query, variables }),
    });
    if (!response.ok) throw new Error('Não foi possível consultar o frete agora. Tente novamente ou consulte no checkout.');
    const body = await response.text();
    const boundary = response.headers.get('content-type')?.match(/boundary="?([^";\s]+)/i)?.[1];
    const chunks = boundary ? body.split(`--${boundary}`).flatMap((part) => {
      const start = part.indexOf('{');
      return start < 0 ? [] : [JSON.parse(part.slice(start).trim())];
    }) : [JSON.parse(body)];
    let data = {};
    for (const chunk of chunks) {
      if (chunk.errors?.length) throw new Error('Não foi possível consultar o frete agora. Consulte as opções no checkout.');
      if (chunk.data) data = chunk.data;
      for (const patch of chunk.incremental || []) {
        if (patch.errors?.length) throw new Error('Não foi possível consultar o frete agora.');
        if (patch.path?.[0] === 'cart') data.cart = { ...data.cart, ...patch.data };
      }
    }
    return data;
  }

  async calculate() {
    this.request?.abort();
    const raw = this.input.value.replace(/\D/g, '');
    if (raw.length !== 8) { this.showError('Digite um CEP válido com 8 dígitos.'); return; }
    const request = new AbortController();
    this.request = request;
    const { signal } = request;
    const timeout = setTimeout(() => request.abort(), 45000);
    this.hideError();
    this.results.textContent = 'Consultando frete…';
    this.results.setAttribute('aria-busy', 'true');
    this.button.disabled = true;
    try {
      const [addressResponse, cartResponse] = await Promise.all([
        fetch(`https://viacep.com.br/ws/${raw}/json/`, { signal }),
        fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal, headers: { Accept: 'application/json' } }),
      ]);
      if (!addressResponse.ok || !cartResponse.ok) throw new Error('Não foi possível consultar o frete agora. Tente novamente.');
      const [address, cart] = await Promise.all([addressResponse.json(), cartResponse.json()]);
      if (address.erro || !address.uf) throw new Error('CEP não encontrado. Confira os números e tente novamente.');
      const lines = cart.items.map((item) => ({
        merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity,
        ...(item.selling_plan_allocation ? { sellingPlanId: `gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}` } : {}),
      }));
      if (!cart.items.some((item) => Number(item.variant_id) === this.variantId)) {
        const quantityInput = this.closest('.shopify-section')?.querySelector('input[name="quantity"]');
        lines.push({ merchandiseId: `gid://shopify/ProductVariant/${this.variantId}`, quantity: Math.max(1, parseInt(quantityInput?.value, 10) || 1) });
      }
      const created = await this.graphql(`mutation QuoteCart($input:CartInput!) {
        cartCreate(input:$input) { cart { id cost { totalAmount { amount currencyCode } } } userErrors { field message } }
      }`, { input: {
        lines, discountCodes: (cart.discount_codes || []).filter((code) => code.applicable).map((code) => code.code),
        buyerIdentity: { countryCode: 'BR' },
        delivery: { addresses: [{ selected: true, address: { deliveryAddress: { countryCode: 'BR', provinceCode: address.uf, city: address.localidade, zip: raw } } }] },
      } }, signal);
      if (!created.cartCreate?.cart || created.cartCreate.userErrors.length) throw new Error('Confira a disponibilidade das peças. O frete também pode ser consultado no checkout.');
      const quoteCart = created.cartCreate.cart;
      const quoted = await this.graphql(`query QuoteRates($id:ID!) {
        cart(id:$id) { ... @defer { deliveryGroups(first:10,withCarrierRates:true) {
          nodes { deliveryOptions { title description code estimatedCost { amount currencyCode } } }
        } } }
      }`, { id: quoteCart.id }, signal);
      const groups = quoted.cart?.deliveryGroups?.nodes || [];
      if (groups.length !== 1 || !groups[0].deliveryOptions.length) throw new Error('Não encontramos uma cotação para esta combinação de peças e CEP. Consulte as opções no checkout.');
      const rates = groups[0].deliveryOptions;
      const freeShipping = rates.some((rate) => Number(rate.estimatedCost.amount) === 0 && /frete gr[aá]tis/i.test(rate.title));
      const options = rates.map((rate) => ({
        name: rate.title,
        price: Number(rate.estimatedCost.amount) === 0 ? 'Grátis' : brl.format(Number(rate.estimatedCost.amount)),
        days: rate.description || 'Prazo confirmado no checkout',
        highlight: Number(rate.estimatedCost.amount) === 0,
      }));
      const d = this.dataset;
      const isLocal = Number(raw) >= Number(d.localCepStart) && Number(raw) <= Number(d.localCepEnd) && (d.localVariants || '').split(',').map(Number).includes(this.variantId);
      if (isLocal && d.localName) options.push({ name: d.localName, price: brl.format(Number(d.localPrice)), days: `${d.localDays} · sujeito ao endereço e estoque de todas as peças`, highlight: false });
      if (isLocal && d.pickupName) options.push({ name: d.pickupName, price: 'Grátis', days: `${d.pickupDays} · conforme estoque`, highlight: false });
      if (!signal.aborted) this.renderResults(options, freeShipping, Number(quoteCart.cost.totalAmount.amount));
    } catch (error) {
      if (this.request === request && (!signal.aborted || this.input.value.replace(/\D/g, '') === raw)) {
        this.showError(signal.aborted ? 'A consulta demorou mais que o esperado. Tente novamente ou consulte no checkout.' : error.message);
      }
    } finally {
      clearTimeout(timeout);
      if (this.request === request) { this.request = null; this.button.disabled = false; this.results.setAttribute('aria-busy', 'false'); }
    }
  }

  renderResults(options, freeShipping, total) {
    let html = freeShipping ? '<div class="sc__free-banner"><span>Com estas peças, sua compra ganha <strong>frete grátis</strong></span></div>' : '';
    if (!freeShipping && this.threshold > total) html += `<div class="sc__free-banner sc__free-banner--pending"><span>Faltam <strong>${brl.format(this.threshold - total)}</strong> para ganhar <strong>frete grátis</strong></span></div>`;
    for (const option of options) {
      html += `<div class="sc__option${option.highlight ? ' sc__option--free' : ''}"><div class="sc__option-info"><span class="sc__option-name">${escapeShippingText(option.name)}</span><span class="sc__option-days">${escapeShippingText(option.days)}</span></div><span class="sc__option-price${option.highlight ? ' sc__option-price--free' : ''}">${escapeShippingText(option.price)}</span></div>`;
    }
    html += '<p class="sc__note">Cotação para as peças do carrinho e a peça selecionada, quando ainda não estiver no carrinho. Postagem em até 3 dias úteis após o pagamento. Valores, prazos e disponibilidade confirmados no checkout.</p>';
    this.results.innerHTML = html;
  }

  showError(message) { this.error.textContent = message; this.error.hidden = false; this.results.replaceChildren(); }
  hideError() { this.error.textContent = ''; this.error.hidden = true; }
}

if (!customElements.get('shipping-calculator')) customElements.define('shipping-calculator', ShippingCalculator);
